import React, { useState } from 'react';
import { ShipAgentLogModal } from '../../components/Modal/ShipAgentLogModal';
import { ArrowUpRight, MonitorSmartphone, Radar } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import api from '../../components/services/apiService';
import Project from '../../common/Utils/project';
import { usePermissions } from '../../hooks/usePermissions';
import { useRemoteAccess } from '../../hooks/useRemoteAccess';
import RemoteAccessModal from '../../components/Modal/RemoteAccessModal';

type DeviceData = {
  deviceName: string;
  hostname: string;
  groupName: string;
  policy: string;
  lastUser: string;
  lastLoginTime: string;
  agentId: string;
  agentLogonAs: string;
  macAddress: string;
};

interface InfoCardProps {
  deviceData: DeviceData;
}

const InfoCard: React.FC<InfoCardProps> = ({ deviceData }) => {
  const [showModal, setShowModal] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const isDev: boolean = Project.api.includes('dev') || Project.api.includes('localhost');
  const { canEdit } = usePermissions();
  const canManageDevices = canEdit('deviceManagement');
  const remoteAccess = useRemoteAccess(selectedTenantId, deviceData?.agentId);
  const [showRemoteAccessModal, setShowRemoteAccessModal] = useState(false);

  // Splashtop RMM (AgentAPI + agent) when enabled for the tenant; otherwise the legacy Marketplace launch URL.
  function handleRemoteAccess() {
    if (!remoteAccess.featureLoaded) return;
    if (remoteAccess.isRmmEnabled) {
      setShowRemoteAccessModal(true);
      // Called synchronously from the click so the web client's pre-opened tab isn't popup-blocked.
      remoteAccess.start();
      return;
    }
    handleLegacyRemoteAccess();
  }

  function closeRemoteAccessModal() {
    setShowRemoteAccessModal(false);
    remoteAccess.reset();
  }

  const rmmUnavailableReason =
    remoteAccess.isRmmEnabled && remoteAccess.agentStatus && !remoteAccess.agentStatus.canConnect
      ? remoteAccess.agentStatus.message || 'Remote access is not available for this device right now.'
      : undefined;
  const remoteAccessBusy = isConnecting || remoteAccess.isBusy;
  const remoteAccessDisabled = remoteAccessBusy || !remoteAccess.featureLoaded || Boolean(rmmUnavailableReason);
  const remoteAccessLabel = remoteAccess.isBusy ? 'Connecting...' : isConnecting ? 'Checking...' : 'Remote Access';

  async function handleLegacyRemoteAccess() {
    setIsConnecting(true);
    try {
      // Step 1: check if device is online in Splashtop before launching
      // deviceName maps to OS computer name, which matches Splashtop's host_name field
      const hostname = deviceData?.deviceName;
      if (!hostname) {
        addToast('Device name not available for this device', 'error');
        return;
      }

      const statusResponse = await api.get(`marketplaceservice/Splashtop/device-status/${selectedTenantId}`, {
        params: { hostname },
      });

      if (!statusResponse.data.isSuccess) {
        addToast(statusResponse.data.message || 'Failed to check device status', 'error');
        return;
      }

      const statusData = statusResponse.data.data;

      if (!statusData?.checkSupported) {
        // Tenant doesn't have Splashtop Enterprise API — skip online check and launch directly
        addToast('Online status check not available for your plan. Attempting to connect...', 'info');
      } else {
        if (!statusData?.deviceFound) {
          addToast('Device not found in Splashtop. Please ensure the Splashtop streamer is installed.', 'error');
          return;
        }

        if (!statusData?.isOnline) {
          const lastOnline = statusData?.lastOnline;
          addToast(lastOnline ? `Device is offline. Last seen: ${lastOnline}` : 'Device is currently offline.', 'error');
          return;
        }
      }

      // Step 2: get launch URL and redirect
      const response = await api.get(`marketplaceservice/Splashtop/launch-url/${selectedTenantId}`);

      if (response.data.isSuccess && response.data.data) {
        const splashtopData = response.data.data;
        let launchUrl = splashtopData.launchUrl;

        // Get MAC address from deviceData, remove colons
        const macAddress = deviceData?.macAddress;
        if (!macAddress) {
          addToast('MAC address not available for this device', 'error');
          return;
        }
        const cleanMacAddress = macAddress.replace(/:/g, '');

        // Replace {macAddress} placeholder with actual MAC address
        launchUrl = launchUrl.replace('{macAddress}', cleanMacAddress);

        // Open the URL
        window.location.href = launchUrl;
      } else {
        addToast(response.data.message || 'Failed to get remote access URL', 'error');
      }
    } catch (error: any) {
      console.error('Error getting remote access URL:', error);
      addToast(error?.response?.data?.message || 'Failed to get remote access URL', 'error');
    } finally {
      setIsConnecting(false);
    }
  }

  async function handleDiscoverNearbyAgents() {
    setIsDiscovering(true);
    try {
      const tenantId = selectedTenantId;
      const agentId = deviceData?.agentId;

      if (!tenantId || !agentId) {
        addToast('Missing tenant or agent information', 'error');
        return;
      }

      const response = await api.post(`${Project.api}patchcenterservice/RemediationPlan/${tenantId}/discover-nearby-agent`, {
        agentIdList: [agentId],
      });

      if (response.data.isSuccess) {
        addToast(response.data.message || 'Nearby agent discovery initiated successfully', 'success');
      } else {
        addToast(response.data.message || 'Failed to initiate discovery', 'error');
      }
    } catch (error: any) {
      console.error('Error discovering nearby agents:', error);
      addToast(error?.response?.data?.message || 'Failed to initiate nearby agent discovery', 'error');
    } finally {
      setIsDiscovering(false);
    }
  }

  return (
    <div className="bg-[#1C1C1D] text-white rounded-xl p-6 w-full shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">{deviceData?.deviceName}</h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-300">
            <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5.121 17.804A4 4 0 016.4 5.372M18.879 17.804A4 4 0 0017.6 5.372"
                ></path>
              </svg>
            </div>
            <div>
              <span className="block text-xs text-gray-400">Hostname</span>
              <span className="text-sm font-medium">{deviceData?.hostname}</span>
            </div>
          </div>
        </div>

        {canManageDevices && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleRemoteAccess}
              disabled={remoteAccessDisabled}
              title={rmmUnavailableReason}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-800 transition-transform transform hover:scale-105 px-5 py-2.5 rounded-md font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <MonitorSmartphone size={16} />
              <span>{remoteAccessLabel}</span>
            </button>
            {deviceData?.agentLogonAs !== 'LocalAccount' &&
              deviceData?.agentLogonAs !== 'LocalSystem' &&
              deviceData?.agentLogonAs !== null && (
                <button
                  onClick={handleDiscoverNearbyAgents}
                  disabled={isDiscovering}
                  className="flex items-center gap-2 bg-red-700 hover:bg-red-800 transition-transform transform hover:scale-105 px-5 py-2.5 rounded-md font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDiscovering ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Discovering...</span>
                    </>
                  ) : (
                    <>
                      <Radar size={16} />
                      <span>Discover Nearby Agents</span>
                    </>
                  )}
                </button>
              )}

            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-800 transition-transform transform hover:scale-105 px-5 py-2.5 rounded-md font-medium text-sm"
            >
              <span>Ship Agent Log</span>
              <ArrowUpRight size={16} />
            </button>
          </div>
        )}
      </div>

      <hr className="border-gray-700 mb-4" />

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-400">Group</p>
          <p className="text-white">{deviceData?.groupName || 'No group'}</p>
        </div>

        <div className="md:col-span-2">
          <p className="text-gray-400 flex items-center gap-1">
            Last User Login <span className="text-green-400 text-xs">●</span>
          </p>
          <p className="text-white">{deviceData?.lastLoginTime ? new Date(deviceData.lastLoginTime).toLocaleString() : 'N/A'}</p>
        </div>
      </div>

      {/* Modal */}
      {showModal && <ShipAgentLogModal show={showModal} onClose={() => setShowModal(false)} agentId={deviceData?.agentId} />}
      <RemoteAccessModal
        show={showRemoteAccessModal}
        deviceName={deviceData?.deviceName}
        state={remoteAccess.state}
        onClose={closeRemoteAccessModal}
        onRetry={remoteAccess.start}
        onRelaunch={remoteAccess.relaunch}
      />
    </div>
  );
};

export default InfoCard;
